import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, Paperclip, Send, X } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../lib/api';
import FeatureHeader from '../components/FeatureHeader';

const CLOUDINARY_UPLOAD_URL = 'https://api.cloudinary.com/v1_1/dp09q5por/auto/upload';

export default function ConsultationChats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId');

  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [chatExpiresAt, setChatExpiresAt] = useState(null);
  const [chatExpired, setChatExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  const [currentConsultation, setCurrentConsultation] = useState(null);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);
  const sharedRecordsSentRef = useRef(false);

  const sharedRecords = location.state?.sharedRecords || [];
  const shareMessage = location.state?.shareMessage || 'Sharing my health records with you.';

  const loadSessions = useCallback(async () => {
    if (!userId) return;
    const response = await api.get('/api/consultations/chat-sessions', { params: { userId } });
    setSessions(response.data?.consultations || []);
  }, [userId]);

  const loadMessages = useCallback(async () => {
    if (!id) return;
    const response = await api.get(`/api/consultations/${id}/chat`);
    const data = response.data || {};
    setMessages(data.chat || []);
    setChatExpiresAt(data.chatExpiresAt ? new Date(data.chatExpiresAt) : null);
    setChatExpired(!!data.chatExpired);
    setLoading(false);
  }, [id]);

  const uploadToCloudinary = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'profile_preset');

    const response = await fetch(CLOUDINARY_UPLOAD_URL, { method: 'POST', body: formData });
    const data = await response.json();

    if (!response.ok || !data?.secure_url) {
      throw new Error(data?.error?.message || 'File upload failed');
    }

    return {
      name: file.name,
      url: data.secure_url,
      mimeType: file.type || 'application/octet-stream',
      size: file.size || 0,
      type: file.type?.startsWith('image/') ? 'image' : 'file',
    };
  }, []);

  useEffect(() => {
    async function fetchSessions() {
      try {
        await loadSessions();
      } catch (error) {
        console.error('Failed to load sessions:', error);
        setSessions([]);
      }
    }
    fetchSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    async function fetchMessages() {
      try {
        await loadMessages();
      } catch (error) {
        console.error('Failed to load messages:', error);
        setLoading(false);
      }
    }

    fetchMessages();
    const refresh = setInterval(() => {
      loadMessages().catch((error) => {
        console.error('Failed to refresh messages:', error);
      });
    }, 4000);

    return () => clearInterval(refresh);
  }, [id, loadMessages]);

  const autoSendSharedRecords = useCallback(async () => {
    if (!id || !userId || sharedRecords.length === 0 || sharedRecordsSentRef.current) return;
    
    const recordsText = sharedRecords.map((record, index) => {
      return `${index + 1}. ${record.title || 'Health Record'} (${record.type || 'Record'} - ${record.date ? new Date(record.date).toLocaleDateString() : 'Recent'})`;
    }).join('\n');

    const recordIds = sharedRecords.map(r => r.id || r._id).join(',');
    const baseUrl = window.location.origin;
    const viewLink = `${baseUrl}/shared-records/${recordIds}?patientId=${userId}`;

    const fullMessage = `${shareMessage}\n\nShared Health Records:\n${recordsText}\n\n📋 View full records: ${viewLink}`;

    try {
      const response = await api.post(`/api/consultations/${id}/chat`, {
        text: fullMessage,
        senderName: localStorage.getItem('userName') || 'User',
        attachments: [],
      });

      setMessages((current) => [...current, response.data.message]);
      sharedRecordsSentRef.current = true;
    } catch (error) {
      console.error('Failed to auto-send shared records:', error);
    }
  }, [id, userId, sharedRecords, shareMessage]);

  useEffect(() => {
    if (loading || sharedRecords.length === 0) return;
    autoSendSharedRecords();
  }, [loading, sharedRecords, autoSendSharedRecords]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!chatExpiresAt) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diff = chatExpiresAt.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft('Expired');
        setChatExpired(true);
        clearInterval(interval);
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
    }, 1000);
    return () => clearInterval(interval);
  }, [chatExpiresAt]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    api.get(`/api/consultations/${id}`).then((res) => {
      if (mounted) setCurrentConsultation(res.data || null);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [id]);

  const handleFileSelection = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    if (!nextFiles.length) return;
    setSelectedFiles((current) => [...current, ...nextFiles]);
    event.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const send = async (event) => {
    event?.preventDefault();
    if (chatExpired) return;
    const text = draft.trim();
    if ((!text && selectedFiles.length === 0) || !id || !userId) return;

    setUploading(true);

    try {
      const uploadedAttachments = await Promise.all(
        selectedFiles.map(async (file) => uploadToCloudinary(file))
      );

      const response = await api.post(`/api/consultations/${id}/chat`, {
        text,
        senderName: localStorage.getItem('userName') || 'User',
        attachments: uploadedAttachments,
      });

      setMessages((current) => [...current, response.data.message]);
      setDraft('');
      setSelectedFiles([]);
      loadSessions().catch(() => {});
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Your message could not be sent.');
    } finally {
      setUploading(false);
    }
  };

  const renderAttachment = (attachment, index) => {
    const url = attachment?.url;
    if (!url) return null;

    const isImage = attachment?.mimeType?.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(attachment?.name || attachment?.url || '');

    return (
      <div key={`${url}-${index}`} className="mt-2">
        {isImage ? (
          <img
            src={url}
            alt={attachment?.name || 'Shared attachment'}
            style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, display: 'block' }}
          />
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="d-inline-flex align-items-center gap-2 text-decoration-none"
            style={{
              borderRadius: 10,
              background: 'rgba(255,255,255,0.16)',
              padding: '8px 10px',
              color: 'inherit',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Paperclip size={14} />
            <span>{attachment?.name || 'Shared file'}</span>
          </a>
        )}
      </div>
    );
  };

  const renderMessageText = (text, isMine) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        const url = part;
        return (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ color: isMine ? '#fff' : '#704af6', textDecoration: 'underline', wordBreak: 'break-all' }}
          >
            {url}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (!id) return (
    <section className="container" style={{ maxWidth: 680, paddingTop: 16 }}>
      <FeatureHeader
        title="Consultation chats"
        subtitle="Your booked doctor chats"
        onBack={() => navigate('/doctor-consult')}
      />
      
      {sharedRecords.length > 0 && (
        <div style={{ 
          background: '#f5f3ff', 
          border: '1px solid #704af6', 
          borderRadius: 12, 
          padding: 12, 
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <div style={{ fontSize: 20 }}>📎</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
              Sharing {sharedRecords.length} record{sharedRecords.length !== 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              Select a doctor below to share these records with them
            </div>
          </div>
        </div>
      )}
      
      {sessions.length === 0 ? (
        <p className="text-muted mt-4">You have no booked chat consultations yet.</p>
      ) : (
        sessions.map((session) => {
          const sessionExpiresAt = new Date(new Date(session.appointmentTime).getTime() + 48 * 60 * 60 * 1000);
          const sessionExpired = new Date() > sessionExpiresAt;
          return (
            <button
              key={session._id}
              type="button"
              onClick={() => navigate(`/consultation-chats/${session._id}`, { 
                state: { 
                  sharedRecords: sharedRecords.length > 0 ? sharedRecords : undefined,
                  shareMessage: shareMessage,
                } 
              })}
              className="w-100 text-start mt-3"
              style={{ border: '1px solid #eee', background: '#fff', borderRadius: 14, padding: 14 }}
            >
              <div style={{ fontWeight: 700 }}>{session.doctor_?.name || 'Doctor'}</div>
              <small className="text-muted">
                {session.reason || 'Chat consultation'} · {new Date(session.appointmentTime).toLocaleString()}
                {sessionExpired ? ' · Expired' : ''}
              </small>
            </button>
          );
        })
      )}
    </section>
  );

  return (
    <section className="container d-flex flex-column" style={{ maxWidth: 680, height: '100dvh', paddingTop: 16 }}>
      <FeatureHeader
        title="Doctor chat"
        subtitle="Your consultation messages are saved here"
        onBack={() => navigate('/consultation-chats')}
      />

      {chatExpiresAt && (
        <div
          className="text-center py-2 mb-2"
          style={{
            background: chatExpired ? '#fff3cd' : '#f0edf7',
            color: chatExpired ? '#856404' : '#4b4a58',
            borderRadius: 12,
            fontWeight: 600,
            fontSize: 13,
            border: `1px solid ${chatExpired ? '#ffc107' : '#e5dff3'}`,
          }}
        >
          {chatExpired ? 'Chat session expired — viewing only' : `Chat available for: ${timeLeft}`}
          {chatExpired && (() => {
            const doctor = currentConsultation?.doctor_ || sessions.find((s) => s._id === id)?.doctor_;
            if (!doctor) return null;
            return (
              <button
                type="button"
                onClick={() => navigate('/booking-screen', { state: doctor })}
                className="btn btn-sm ms-3"
                style={{ background: 'var(--primary)', color: '#fff', borderRadius: 10, padding: '4px 12px' }}
              >
                Book Again
              </button>
            );
          })()}
        </div>
      )}

      <div className="flex-grow-1 overflow-auto py-3">
        {loading ? (
          <p className="text-muted">Loading chat…</p>
        ) : messages.length === 0 ? (
          <p className="text-muted">Start the conversation with your doctor.</p>
        ) : (
          messages.map((message, index) => {
            const mine = String(message.senderId) === String(userId);
            return (
              <div
                key={message._id || `${message.sentAt || index}-${index}`}
                className="d-flex mb-2"
                style={{ justifyContent: mine ? 'flex-end' : 'flex-start' }}
              >
                <div
                  style={{
                    maxWidth: '78%',
                    padding: '10px 12px',
                    borderRadius: 14,
                    background: mine ? 'var(--primary)' : '#f0edf7',
                    color: mine ? '#fff' : '#251e36',
                  }}
                >
                  {message.text ? <div>{renderMessageText(message.text, mine)}</div> : null}
                  {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                    <div>
                      {message.attachments.map((attachment, attachmentIndex) => renderAttachment(attachment, attachmentIndex))}
                    </div>
                  ) : null}
                  <small style={{ opacity: 0.75, display: 'block', marginTop: 6 }}>
                    {message.senderName || (mine ? 'You' : 'Doctor')}
                  </small>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {selectedFiles.length > 0 && (
        <div className="d-flex flex-wrap gap-2 py-2">
          {selectedFiles.map((file, index) => (
            <div key={`${file.name}-${index}`} className="d-flex align-items-center gap-2 px-2 py-1" style={{ borderRadius: 999, background: '#f4f0ff' }}>
              <span style={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </span>
              <button type="button" className="btn btn-link p-0" onClick={() => removeSelectedFile(index)} style={{ color: '#5b3ac2' }}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={send} className="d-flex gap-2 py-3">
        <button
          type="button"
          className="btn"
          onClick={() => !chatExpired && fileInputRef.current?.click()}
          style={{ background: '#f0edf7', color: 'var(--primary)', border: '1px solid #e5dff3', opacity: chatExpired ? 0.5 : 1 }}
          aria-label="Attach file"
          disabled={chatExpired}
        >
          <Paperclip size={18} />
        </button>
        <input ref={fileInputRef} type="file" hidden multiple onChange={handleFileSelection} />
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="form-control"
          placeholder={chatExpired ? 'Chat session expired' : 'Write a message…'}
          disabled={chatExpired}
        />
        <button
          type="submit"
          className="btn"
          style={{ background: 'var(--primary)', color: '#fff', opacity: uploading || chatExpired ? 0.7 : 1 }}
          aria-label="Send message"
          disabled={uploading || chatExpired}
        >
          <Send size={18} />
        </button>
      </form>
    </section>
  );
}